import gql from "graphql-tag";
import { Props as IProjectListItemProps } from "../components/ProjectListItem";
import { apolloClient } from "utils";
import { ProjectStatus } from "types/ProjectStatus";

export async function get(): Promise<IProjectListItemProps[]> {
  try {
    const response = await apolloClient.query({ query: GET });
    if (response.errors || response.data == null || response.data.projects == null) throw response.errors;
    const backendProjects: IBackendProject[] = response.data.projects;
    return backendProjects.map(mapBackendProject);
  } catch (error) {
    console.log(error);
    return [];
  }
}

const GET = gql`
  query projects {
    projects(limit: 3, where: { status: { in: ["preview", "soon", "invest"] }, isHidden: { is: FALSE } }, orderBy: firstEmissionStartDateTime_DESC) {
      id
      name
      projectOwnerCompanyName
      status
      submittedAmountInCt
      requiredBudgetInCt
      requiredMinBudgetInCt
      submittedAmountInCt
      investorCount
      coFundingAmountInCt
      latestEmissionEndDateTime
      slug
      characteristics(orderBy: position_ASC) {
        text
      }
      emissions {
        parameters: executableRepaymentPlanFunctionParameterValues
        investProcess {
          id
        }
      }
      attachments(where: { type: { equals: "heroImage" } }, limit: 1) {
        id
        files {
          fileName
          objects {
            presignedGetUrl
          }
        }
      }
    }
  }
`;

function mapBackendProject(project: IBackendProject): IProjectListItemProps {
  const parameters = getRepaymentParameters(project.emissions);

  return {
    backgroundImageUrl: project.attachments?.[0]?.files?.[0]?.objects?.[0]?.presignedGetUrl,
    requiredBudgetInCt: project.requiredBudgetInCt,
    interestRateInPct: parameters.interestRate,
    contractDurationPeriods: parameters.contractDurationPeriods,
    projectName: project.name,
    companyName: project.projectOwnerCompanyName,
    characteristics: project.characteristics?.map(({ text }) => text) ?? [],
    investorCount: project.investorCount,
    requiredMinBudgetInCt: project.requiredMinBudgetInCt,
    submittedAmountInCt: project.submittedAmountInCt,
    coFundingAmountInCt: project.coFundingAmountInCt,
    endDateTime: project.latestEmissionEndDateTime,
    slug: project.slug,
    investProcessId: project.emissions?.[0]?.investProcess?.id
  };
}

function getRepaymentParameters(emissions: IBackendEmission[]) {
  const parametersList = emissions.map(({ parameters }) => parameters);

  const contractDurationPeriods = parametersList?.[0]?.contractDurationPeriods;
  const gracePeriods = parametersList?.[0]?.gracePeriods;
  const initialInterestRate = parametersList?.[0]?.initialInterestRate;

  return { contractDurationPeriods, gracePeriods, initialInterestRate, ...getInterestRates(parametersList) };
}

function getInterestRates(parametersList: any): { interestRate?: number; earlyBirdBonusRate?: number } {
  const interestRates = parametersList
    .map((parameters) => parameters.interestRate)
    .filter((item) => item != null)
    .sort((a, b) => a - b);

  const interestRate = interestRates[0];
  const diff = interestRates[interestRates.length - 1] - interestRates[0];
  const earlyBirdBonusRate = diff !== 0 ? diff : undefined;

  return { interestRate, earlyBirdBonusRate };
}

interface IBackendProject {
  id: string;
  name: string;
  projectOwnerCompanyName?: string;
  status: ProjectStatus;
  requiredBudgetInCt: number;
  requiredMinBudgetInCt?: number;
  submittedAmountInCt: number;
  investorCount: number;
  coFundingAmountInCt: number;
  latestEmissionEndDateTime?: string;
  slug?: string;
  characteristics: Array<{
    text: string;
  }>;
  emissions: IBackendEmission[];
  attachments: Array<{
    id: string;
    files: Array<{
      fileName: string;
      objects: Array<{
        presignedGetUrl: string;
      }>;
    }>;
  }>;
}

interface IBackendEmission {
  parameters?: {
    interestRate: number;
    contractDurationPeriods: number;
    initialInterestRate: number;
    gracePeriods?: number;
  };
  investProcess?: {
    id: string;
  };
}
